import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100dvh;
  background: #1a1a2e;
  color: #e0e0e0;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const PanelTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.3px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
`;

const Section = styled.fieldset`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 12px;
  margin: 0 0 14px 0;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionLegend = styled.legend`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #888;
  padding: 0 6px;
`;

const FormGroup = styled.div`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #aaa;
  font-size: 11px;
`;

const inputStyles = `
  width: 100%;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  font-size: 12px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.06);
  color: #e0e0e0;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: #5b8def;
  }

  &::placeholder {
    color: #666;
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const Select = styled.select`
  ${inputStyles}
  appearance: auto;
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorSwatch = styled.input`
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 5px;
  padding: 2px;
  cursor: pointer;
  background: transparent;
  flex-shrink: 0;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: none;
    border-radius: 3px;
  }
`;

const ColorHex = styled(Input)`
  flex: 1;
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #ccc;
`;

const Checkbox = styled.input`
  margin: 0;
  accent-color: #5b8def;
`;

const PanelFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: 8px 0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: opacity 0.15s;

  background: ${({ $variant }) =>
    $variant === 'danger'
      ? '#e04848'
      : $variant === 'secondary'
        ? 'rgba(255,255,255,0.1)'
        : '#5b8def'};
  color: ${({ $variant }) => ($variant === 'secondary' ? '#ccc' : '#fff')};

  &:hover {
    opacity: 0.85;
  }
`;

const Fab = styled.button`
  position: fixed;
  bottom: 16px;
  right: 16px;
  background: #1a1a2e;
  color: #aaa;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  z-index: 998;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const HeaderVariantToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const HeaderVariantButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) =>
    $active ? 'rgba(91, 141, 239, 0.15)' : 'rgba(255, 255, 255, 0.06)'};
  color: ${({ $active }) => ($active ? '#5b8def' : '#888')};
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(91, 141, 239, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  transition: all 0.15s;

  &:hover {
    color: ${({ $active }) => ($active ? '#5b8def' : '#ccc')};
  }
`;

interface DevFormProps {
  isVisible: boolean;
  onToggle: () => void;
  currentVariant: 'hosted' | 'embed';
}

function switchVariant(target: 'hosted' | 'embed') {
  const params = window.location.search;
  if (target === 'embed') {
    window.location.href = `/embed-test.html${params}`;
  } else {
    window.location.href = `/${params}`;
  }
}

/** Easily modify initialization data on localhost */
export const DevForm: React.FC<DevFormProps> = ({
  isVisible,
  onToggle,
  currentVariant,
}) => {
  const [formData, setFormData] = useState({
    business_name: '',
    theme: '#2D597B',
    happy_message: '',
    unhappy_message: '',
    unhappy_contact: '',
    unhappy_form_only: false,
    enabled_providers: '',
    stars_cutoff: '',
    rating_enabled: false,
    site: '',
    google_url: '',
    yelp_url: '',
    facebook_url: '',
    tripadvisor_url: '',
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setFormData({
      business_name: urlParams.get('business_name') || '',
      theme: urlParams.get('theme') || '#2D597B',
      happy_message: urlParams.get('happy_message') || '',
      unhappy_message: urlParams.get('unhappy_message') || '',
      unhappy_contact: urlParams.get('unhappy_contact') || '',
      unhappy_form_only: urlParams.get('unhappy_form_only') === 'true',
      enabled_providers: urlParams.get('enabled_providers') || '',
      stars_cutoff: urlParams.get('stars_cutoff') || '',
      rating_enabled: urlParams.get('rating_enabled') === 'true',
      site: urlParams.get('site') || '',
      google_url: urlParams.get('google_url') || '',
      yelp_url: urlParams.get('yelp_url') || '',
      facebook_url: urlParams.get('facebook_url') || '',
      tripadvisor_url: urlParams.get('tripadvisor_url') || '',
    });
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'unhappy_form_only'
            ? value === 'true'
            : value,
    }));
  };

  const handleColorChange = (hex: string) => {
    setFormData((prev) => ({ ...prev, theme: hex }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('r', 'preview');
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== '' && value !== false) {
        params.set(key, value.toString());
      }
    });
    window.location.search = params.toString();
  };

  const handleDemo = () => {
    window.location.search = '?r=demo';
  };

  const handleClear = () => {
    window.location.search = '';
  };

  if (!isVisible) {
    return <Fab onClick={onToggle}>Dev Preview</Fab>;
  }

  return (
    <>
      <Overlay onClick={onToggle} />
      <Panel>
        <PanelHeader>
          <PanelTitle>Widget Preview</PanelTitle>
          <HeaderVariantToggle>
            <HeaderVariantButton
              $active={currentVariant === 'hosted'}
              onClick={() =>
                currentVariant !== 'hosted' && switchVariant('hosted')
              }
            >
              Hosted
            </HeaderVariantButton>
            <HeaderVariantButton
              $active={currentVariant === 'embed'}
              onClick={() =>
                currentVariant !== 'embed' && switchVariant('embed')
              }
            >
              Embed
            </HeaderVariantButton>
          </HeaderVariantToggle>
          <CloseButton onClick={onToggle}>&times;</CloseButton>
        </PanelHeader>

        <PanelBody>
          <form id="dev-preview-form" onSubmit={handleSubmit}>
            <Section>
              <SectionLegend>Branding</SectionLegend>
              <FormGroup>
                <Label>Business Name</Label>
                <Input
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="Acme Restaurant"
                />
              </FormGroup>
              <FormGroup>
                <Label>Theme Color</Label>
                <ColorRow>
                  <ColorSwatch
                    type="color"
                    value={formData.theme}
                    onChange={(e) => handleColorChange(e.target.value)}
                  />
                  <ColorHex
                    name="theme"
                    value={formData.theme}
                    onChange={handleInputChange}
                    placeholder="#2D597B"
                    maxLength={7}
                  />
                </ColorRow>
              </FormGroup>
            </Section>

            <Section>
              <SectionLegend>Messages</SectionLegend>
              <FormGroup>
                <Label>Happy Message</Label>
                <Input
                  name="happy_message"
                  value={formData.happy_message}
                  onChange={handleInputChange}
                  placeholder="Thanks for the great review!"
                />
              </FormGroup>
              <FormGroup>
                <Label>Unhappy Message</Label>
                <Input
                  name="unhappy_message"
                  value={formData.unhappy_message}
                  onChange={handleInputChange}
                  placeholder="We'd love to make this right"
                />
              </FormGroup>
              <FormGroup>
                <Label>Unhappy Contact</Label>
                <Input
                  name="unhappy_contact"
                  value={formData.unhappy_contact}
                  onChange={handleInputChange}
                  placeholder="contact@business.com"
                />
              </FormGroup>
              <FormGroup>
                <Label>Unhappy Rating Behavior</Label>
                <Select
                  name="unhappy_form_only"
                  value={formData.unhappy_form_only ? 'true' : 'false'}
                  onChange={handleInputChange}
                >
                  <option value="false">
                    Show Review Providers & Contact Form
                  </option>
                  <option value="true">Show Contact Form Only</option>
                </Select>
              </FormGroup>
            </Section>

            <Section>
              <SectionLegend>Rating</SectionLegend>
              <FormGroup>
                <Label>Enabled Providers</Label>
                <Input
                  name="enabled_providers"
                  value={formData.enabled_providers}
                  onChange={handleInputChange}
                  placeholder="google,yelp,facebook"
                />
              </FormGroup>
              <FormGroup>
                <Label>Stars Cutoff</Label>
                <Select
                  name="stars_cutoff"
                  value={formData.stars_cutoff}
                  onChange={handleInputChange}
                >
                  <option value="">Default</option>
                  <option value="3">3 stars</option>
                  <option value="4">4 stars</option>
                  <option value="5">5 stars</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <CheckboxRow>
                  <Checkbox
                    type="checkbox"
                    name="rating_enabled"
                    checked={formData.rating_enabled}
                    onChange={handleInputChange}
                  />
                  Rating Enabled
                </CheckboxRow>
              </FormGroup>
              <FormGroup>
                <Label>Site ID</Label>
                <Input
                  name="site"
                  value={formData.site}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </FormGroup>
            </Section>

            <Section>
              <SectionLegend>Provider URLs</SectionLegend>
              <FormGroup>
                <Label>Google</Label>
                <Input
                  name="google_url"
                  value={formData.google_url}
                  onChange={handleInputChange}
                  placeholder="https://g.page/..."
                />
              </FormGroup>
              <FormGroup>
                <Label>Yelp</Label>
                <Input
                  name="yelp_url"
                  value={formData.yelp_url}
                  onChange={handleInputChange}
                  placeholder="https://yelp.com/..."
                />
              </FormGroup>
              <FormGroup>
                <Label>Facebook</Label>
                <Input
                  name="facebook_url"
                  value={formData.facebook_url}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/..."
                />
              </FormGroup>
              <FormGroup>
                <Label>TripAdvisor</Label>
                <Input
                  name="tripadvisor_url"
                  value={formData.tripadvisor_url}
                  onChange={handleInputChange}
                  placeholder="https://tripadvisor.com/..."
                />
              </FormGroup>
            </Section>
          </form>
        </PanelBody>

        <PanelFooter>
          <Button type="submit" form="dev-preview-form" $variant="primary">
            Preview
          </Button>
          <Button type="button" $variant="secondary" onClick={handleDemo}>
            Demo
          </Button>
          <Button type="button" $variant="danger" onClick={handleClear}>
            Clear
          </Button>
        </PanelFooter>
      </Panel>
    </>
  );
};
